import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, shareReplay, tap, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SimpleSubscriptionStatus } from '../../shared/interfaces/subscription.interfaces';
import { WebSocketService } from '../websocket/websocket.service';
import { SecureTokenService } from './secure-token.service';
import { environment } from '../../config/cabinet-environment';
import { ThemeService } from '@infinitform/shared-ui';
import { UserService } from './user.service';
import { EulaStatus } from '../../interfaces/eula.interface';

export interface ApplicationUser {
  // Removed sensitive token data - now stored securely
  email: string;
  role: string;
  permissions: string[];
  owned_organization_id?: string;
  member_organization_id?: string;
  first_name?: string;
  last_name?: string;
  subscription_status?: SimpleSubscriptionStatus;
  needs_organization_onboarding?: boolean;
  organization_subscription_expired?: boolean;
  subscription_seat_active?: boolean;
  plugin_features?: string[];
}

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

export interface RegisterRequest {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  phone_number?: string;
  company?: string;
  industry?: string;
  persona?: string;
  marketing_emails_opt_in?: boolean;
}

export interface ActiveSession {
  id: string;
  userAgent: string;
  ipAddress: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface AuthTokenResponse {
  access_token: string;
  expires_in?: number;
  email: string;
  role: string;
  permissions?: string[];
  owned_organization_id?: string;
  member_organization_id?: string;
  first_name?: string;
  last_name?: string;
  subscription_status?: SimpleSubscriptionStatus;
  needs_organization_onboarding?: boolean;
  organization_subscription_expired?: boolean;
  subscription_seat_active?: boolean;
  plugin_features?: string[];
}

export interface OrganizationOnboardingData {
  company_name: string;
  industry?: string;
  size?: string;
  teamEmails?: string[];
  [key: string]: string | string[] | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private router = inject(Router);
  private secureTokenService = inject(SecureTokenService);
  private injector = inject(Injector);

  public currentUser?: ApplicationUser;
  public eulaModalTrigger = new Subject<void>();

  // Cache the refresh token observable to prevent multiple simultaneous calls
  private refreshToken$: Observable<AuthTokenResponse> | null = null;

  constructor() {
    // Load user data (without tokens) from localStorage
    const localStorageUser = localStorage.getItem('currentUser');
    if (localStorageUser) {
      try {
        const userData = JSON.parse(localStorageUser);
        // Only store non-sensitive user data
        this.currentUser = {
          email: userData.email,
          role: userData.role,
          permissions: userData.permissions || [],
          owned_organization_id: userData.owned_organization_id,
          member_organization_id: userData.member_organization_id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          subscription_status: userData.subscription_status,
          needs_organization_onboarding: userData.needs_organization_onboarding,
          organization_subscription_expired: userData.organization_subscription_expired,
          subscription_seat_active: userData.subscription_seat_active,
          plugin_features: userData.plugin_features || []
        };

        // Validate that we have essential user data
        if (!this.currentUser.email || !this.currentUser.role) {
          this.logout();
          this.router.navigate(['/login']);
        }
      } catch (error) {
        this.logout();
        this.router.navigate(['/login']);
      }
    } else {
      this.currentUser = undefined;
    }
  }

  login(email: string | null | undefined, password: string | null | undefined) {
    return this.http
      .post<AuthTokenResponse>('/auth/login', { email, password }, { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) })
      .pipe(
        map((response) => {
          if (response && response.access_token) {
            this.storeTokens(response);
            this.injector.get<WebSocketService>(WebSocketService).connect();
            this.injector.get<ThemeService>(ThemeService).initializeTheme();
          }
          return response;
        })
      );
  }

  /**
   * Initiate Google OAuth login
   */
  loginWithGoogle() {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${environment.API_ADDRESS}/auth/google`;
  }

  /**
   * Handle login response (for 2FA verification completion)
   */
  handleLoginResponse(response: AuthTokenResponse): void {
    if (response && response.access_token) {
      this.storeTokens(response);
      this.injector.get<WebSocketService>(WebSocketService).connect();
      this.injector.get<ThemeService>(ThemeService).initializeTheme();
    }
  }

  private storeTokens(response: AuthTokenResponse): void {
    // Store access token securely in memory
    this.secureTokenService.setAccessToken(
      response.access_token,
      response.expires_in || 86400 // Default 24 hours if not provided
    );

    // Store non-sensitive user data only
    const user: ApplicationUser = {
      email: response.email,
      role: response.role,
      permissions: response.permissions || [],
      owned_organization_id: response.owned_organization_id,
      member_organization_id: response.member_organization_id,
      first_name: response.first_name,
      last_name: response.last_name,
      subscription_status: response.subscription_status,
      needs_organization_onboarding: response.needs_organization_onboarding,
      organization_subscription_expired: response.organization_subscription_expired,
      subscription_seat_active: response.subscription_seat_active,
      plugin_features: response.plugin_features || []
    };

    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUser = user;

    // Refresh token is automatically set as HttpOnly cookie by backend
    // Frontend cannot and should not access refresh token directly
  }

  refreshToken(): Observable<AuthTokenResponse> {
    // If there's already a refresh token request in progress, return the cached observable
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    // Create new refresh token request and cache it
    this.refreshToken$ = this.http
      .post<AuthTokenResponse>(
        '/auth/refresh',
        {},
        {
          context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
          withCredentials: true // Important: Include cookies in request
        }
      )
      .pipe(
        map((response) => {
          this.storeTokens(response);
          this.injector.get<WebSocketService>(WebSocketService).connect();
          return response;
        }),
        catchError((error) => {
          this.logout();
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
        tap({
          next: () => {
            // Clear the cached observable after successful completion
            this.refreshToken$ = null;
          },
          error: () => {
            // Clear the cached observable after error
            this.refreshToken$ = null;
          }
        }),
        shareReplay(1) // Share the result with multiple subscribers
      );

    return this.refreshToken$;
  }

  logout() {
    // Call logout endpoint to revoke refresh token (sent via cookie)
    this.injector.get<WebSocketService>(WebSocketService).disconnect();
    if (this.currentUser) {
      this.http.post('/auth/logout', {}, { withCredentials: true }).subscribe({
        next: () => this.clearTokens(),
        error: () => this.clearTokens(), // Clear tokens even if logout fails
        complete: () => this.clearTokens()
      });
      this.clearTokens();
    } else {
      this.clearTokens();
    }
  }

  clearTokens(): void {
    localStorage.removeItem('currentUser');
    this.injector.get<UserService>(UserService).clearUserData();
    this.secureTokenService.clearAccessToken();
    this.currentUser = undefined;
    // Clear any pending refresh token request
    this.refreshToken$ = null;
  }

  logoutAllDevices(): Observable<{ message: string }> {
    return this.http.post('/auth/logout-all', {}, { withCredentials: true }).pipe(
      map(() => {
        this.clearTokens();
        return { message: 'Logged out from all devices' };
      })
    );
  }

  getActiveSessions(): Observable<{ sessions: ActiveSession[] }> {
    return this.http.get<{ sessions: ActiveSession[] }>('/auth/sessions', { withCredentials: true });
  }

  revokeSession(sessionId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `/auth/sessions/${sessionId}/revoke`,
      {},
      { withCredentials: true }
    );
  }

  revokeAllOtherSessions(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      '/auth/sessions/revoke-others',
      {},
      { withCredentials: true }
    );
  }

  get userEmail() {
    return this.currentUser?.email;
  }

  get userRole() {
    return this.currentUser?.role;
  }

  get isAdmin() {
    return this.currentUser?.role === 'admin';
  }

  get isCompanyAdmin() {
    return this.currentUser?.role === 'company_admin';
  }

  /**
   * Check if user is authenticated with valid tokens
   */
  get isAuthenticated(): boolean {
    return this.currentUser !== null && this.secureTokenService.isTokenValid();
  }

  /**
   * Check if we should attempt token refresh (has user data but no valid token)
   */
  shouldRefreshToken(): boolean {
    return this.currentUser !== null && !this.secureTokenService.isTokenValid();
  }

  /**
   * Get current access token for manual use
   */
  getAccessToken(): string | null {
    return this.secureTokenService.getAccessToken();
  }

  /**
   * Check if user should be redirected to onboarding based on subscription status
   */
  shouldRedirectToOnboarding(): boolean {
    return this.currentUser?.subscription_status === 'none';
  }

  /**
   * Check if user has an expired subscription
   */
  hasExpiredSubscription(): boolean {
    return this.currentUser?.subscription_status === 'expired';
  }

  /**
   * Check if user has restricted access (expired subscription, org subscription expired, or seat inactive)
   * Users with restricted access can only view/open projects but cannot edit/download
   */
  hasRestrictedAccess(): boolean {
    return this.currentUser?.subscription_status === 'expired' ||
           this.currentUser?.organization_subscription_expired === true ||
           this.currentUser?.subscription_seat_active === false;
  }

  /**
   * Check if user's organization subscription is expired
   */
  hasExpiredOrganizationSubscription(): boolean {
    return this.currentUser?.organization_subscription_expired === true;
  }

  /**
   * Check if user's subscription seat is inactive
   */
  hasInactiveSeat(): boolean {
    return this.currentUser?.subscription_seat_active === false;
  }

  /**
   * Get the reason for restricted access (for displaying appropriate message)
   */
  getRestrictedAccessReason(): 'expired' | 'org_expired' | 'seat_inactive' | null {
    if (this.currentUser?.subscription_seat_active === false) {
      return 'seat_inactive';
    }
    if (this.currentUser?.organization_subscription_expired === true) {
      return 'org_expired';
    }
    if (this.currentUser?.subscription_status === 'expired') {
      return 'expired';
    }
    return null;
  }

  /**
   * Get current subscription status
   */
  get subscriptionStatus(): SimpleSubscriptionStatus | undefined {
    return this.currentUser?.subscription_status;
  }

  /**
   * Manually refresh token to get updated subscription status
   * Use this after subscription changes (trial creation, subscription updates, etc.)
   */
  refreshTokenForSubscriptionUpdate(): Observable<AuthTokenResponse> {
    return this.refreshToken();
  }

  getOrganization() {
    return this.http.get<{ id: string; name: string; logo: string; logo_url: string; owner_user: { email: string } }>(`/organization`);
  }

  getOrganizationForMember() {
    return this.http.get<{ name: string; logo_url: string; owner_user: { email: string } }>(`/organization/info`);
  }

  getOrganizationOnboardingInfo() {
    return this.http.get(`/organization/onboarding-info`);
  }

  completeOrganizationOnboarding(data: OrganizationOnboardingData): Observable<unknown> {
    return this.http.post(`/organization/complete-onboarding`, data).pipe(
      // After successful onboarding completion, refresh token to get updated user data
      switchMap((response) => {
        // Refresh token to get updated user data (needs_organization_onboarding flag)
        return this.refreshToken().pipe(
          map(() => response), // Return the original response
          catchError(() => {
            // If token refresh fails, still return the original response
            // The user can manually refresh or the next request will trigger a refresh
            return [response];
          })
        );
      })
    );
  }

  // Feedback
  sendFeedback(message: string) {
    return this.http.post('/feedback', { message });
  }

  // Forgot Password
  forgotPassword(email: string) {
    return this.http.post(
      '/user/forgot-password',
      { email },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  // Reset Password
  resetPassword(token: string, newPassword: string) {
    return this.http.post(
      '/user/reset-password',
      { token, newPassword },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  // Register
  register(
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    phone_number?: string,
    company?: string,
    industry?: string,
    persona?: string,
    marketing_emails_opt_in?: boolean
  ): Observable<{ message: string }> {
    const registerData: RegisterRequest = {
      email,
      password,
      firstname,
      lastname,
      phone_number,
      company,
      industry,
      persona,
      marketing_emails_opt_in
    };

    return this.http.post<{ message: string }>(`/user/register`, registerData);
  }

  // Activate Account
  activateAccount(token: string) {
    return this.http.post(
      '/user/activate',
      { token },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  activateOrganizationMember(token: string, newPassword: string) {
    return this.http.post(
      '/user/organization-member-activation',
      { token, newPassword },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  getMemberSetupInfo(token: string) {
    return this.http.get<{ firstname: string; organizationName: string }>(`/user/member-setup-info?token=${token}`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  // Password Activation Methods
  activatePassword(token: string, newPassword: string) {
    return this.http.post<{ message: string }>(
      '/user/password-activation',
      { token, newPassword },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  getPasswordActivationInfo(token: string) {
    return this.http.get<{ email: string; firstname: string; lastname: string }>(`/user/password-activation-info?token=${token}`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  // Resend Activation Email
  resendActivation(email: string) {
    return this.http.post(
      '/user/resend-activation',
      { email },
      { context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true) }
    );
  }

  // EULA Methods
  getEulaStatus() {
    return this.http.get<EulaStatus>('/user/eula-status');
  }

  acceptEula(version: number) {
    return this.http.post('/user/accept-eula', { version });
  }

  triggerEulaModal() {
    this.eulaModalTrigger.next();
  }
}
